/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  IconX,
  IconLoader2,
  IconPlus,
  IconMinus,
  IconInfoCircle,
  IconPackage,
  IconHistory,
  IconPhoto,
  IconClipboardText,
  IconUserCheck,
  IconUser,
  IconMail,
  IconPhone,
  IconRoute,
  IconCheck,
  IconBox,
  IconLayersIntersect,
  IconTrash,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import {
  useFetchProductManifestViewData,
  useUpdateProductManifestAdminConfirm,
  useAddProductsToManifest,
  useFetchMerchantLocations,
  useProductManifestAddInventoryMutation,
} from "../../../apis/productManifestApis";
import { useProductAllListData } from "../../../apis/promoApis";
import { formatDateWithTime } from "../../../utilities/helper";
import ToggleButton from "../../../components/ToggleButton";
import SelectInput from "../../../components/Select";
import { useValidateSkuStorageMutation, useVerifySkuMutation } from "../../../apis/productApis";
import { useLocationBarcodeVerifyMutation } from "../../../hooks/inventoryHooks";
import NewSelectInput from "../../../components/NewSelectInput";
import NewinputField from "../../../components/NewInput";
import { useLanguage } from "../../../context/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
  id?: string;
  type: "view" | "update";
  fetchData: () => void;
  isSidebar?: boolean;
};

type ProductForm = {
  products: {
    missingQty?: number;
    damageQty?: number;
    receivedQty?: number;
    note?: string;
    adminReason?: string;
    expiryDate?: string;
  }[];
};

type ManifestProduct = {
  damageQty: number;
  productId: string;
  sku: string;
  quantity: number;
  missingQty?: number;
  adminReason?: string;
  clientReason?: string;
  note?: string;
  expiryDate?: string;
  hasExpiry?: boolean;
  location?: string;
  locationType?: string;
  receivedQty?: number;
  reservedQuantity?: number;
  shelvedQty?: number;
};

type StatusHistory = {
  id: string;
  entityId: string;
  entityType: string;
  oldStatus: string | null;
  newStatus: string;
  changedByUserId: string;
  details: string;
  createdAt: string;
};

type ManifestData = {
  manifestShortId: string;
  clientId: string;
  clientName: string;
  clientMobileNo: string;
  clientEmail: string;
  status:
    | "MANIFEST_REQUEST"
    | "UPDATED"
    | "ON_PROCESS"
    | "ASSIGN_TO_STAFF"
    | "VERIFIED"
    | "SENT_TO_CLIENT"
    | "ASSIGN_TO_INVENTORY_STAFF"
    | "ON_HOLD"
    | "CHECKED"
    | "ADD_INVENTORY"
    | "IN_PROCESS"
    | "CLOSED"
    | "COMPLETED";
  products: ManifestProduct[];
  statusHistories?: StatusHistory[];
  createdByType?: string;
};

const Row = ({
  icon,
  label,
  value,
  className = "flex items-center justify-between gap-2 py-1",
  classNameValue = "flex items-center gap-2 text-right justify-end min-w-0",
}: {
  icon?: React.ReactNode;
  label?: string;
  className?: string;
  classNameValue?: string;
  value?: React.ReactNode;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-2 min-w-0">
        {icon && <div className="mt-0.5 text-gray-400 flex-shrink-0">{icon}</div>}
        <span className="font-medium text-xs text-[#212121]">
          {label ? t(label) : ""}{" "}
        </span>
      </div>
      <div className={`${classNameValue}`}>
        <span className="break-all text-xs text-[#212121]/70 font-medium">
          {value}
        </span>
      </div>
    </div>
  );
};

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

const formatQuantity = (value: number | undefined | null): string => {
  return value === 0 || value === undefined || value === null
    ? ""
    : String(value);
};

const AsyncT: React.FC<{ text: string }> = ({ text }) => {
  const { translateText, language } = useLanguage();
  const [translated, setTranslated] = useState(text);

  useEffect(() => {
    let active = true;
    if (language === "en") {
      setTranslated(text);
      return;
    }
    translateText(text).then((res) => {
      if (active) setTranslated(res);
    });
    return () => {
      active = false;
    };
  }, [text, translateText, language]);

  return <>{translated}</>;
};

const ViewProductRequestModal = ({
  open,
  onClose,
  id,
  type,
  fetchData,
  isSidebar,
}: Props) => {
  const roles1 = ["Manifest Details", "Timeline"];
  const { t } = useLanguage();

  const [activeAddInventory, setActiveAddInventory] =
    useState<string>("OVERVIEW");

  const {
    data,
    isLoading,
    refetch: fetchDetails,
  } = useFetchProductManifestViewData(id);

  const manifestData = data as ManifestData;
  const isEditableStatus =
    manifestData?.status === "UPDATED" ||
    manifestData?.status === "ON_PROCESS" ||
    manifestData?.status === "ASSIGN_TO_STAFF" ||
    manifestData?.status === "ASSIGN_TO_INVENTORY_STAFF" ||
    manifestData?.status === "ON_HOLD" ||
    manifestData?.status === "VERIFIED" ||
    manifestData?.status === "ADD_INVENTORY" ||
    (manifestData?.createdByType === "CLIENT" &&
      manifestData?.status === "MANIFEST_REQUEST");

  const { data: products } = useProductAllListData();
  const [newProducts, setNewProducts] = useState<
    { id: number; productId: string }[]
  >([{ id: Date.now(), productId: "" }]);

  const [activeTab, setActiveTab] = useState<string>("OVERVIEW");
  const [isEditingMode, setIsEditingMode] = useState<boolean>(false);
  const isUpdatingMode = type === "update" || isEditingMode;

  const allSkusVerified =
    data?.products?.length > 0 &&
    data?.products?.every((p: any) => {
      const expected = Number(p.quantity ?? 0);
      const received = Number(p.receivedQty ?? p.receivedQuantity ?? 0);
      const missing = Number(p.missingQty ?? 0);
      const damaged = Number(p.damageQty ?? 0);
      return (received + missing + damaged) === expected;
    });

  // Reset newProducts when modal opens with different id
  useEffect(() => {
    if (open) {
      setNewProducts([{ id: Date.now(), productId: "" }]);
      setShowAddProduct(false);
      setUpdatingSkuIndex(null);
      setIsEditingMode(false);
    }
  }, [open, id]);

  if (data?.locations?.length > 0) {
    roles1.push("Locations");
  }

  const prevOpenRef = useRef(open);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      if (type === "update") {
        setActiveTab("CARGO ITEMS");
      } else {
        setActiveTab("OVERVIEW");
      }
    } else if (open && type === "update") {
      setActiveTab("CARGO ITEMS");
    }
    prevOpenRef.current = open;
  }, [open, type]);
  const [showAddProduct, setShowAddProduct] = useState<boolean>(false);
  const [showAddInventoryCard, setShowAddInventoryCard] = useState<boolean>(false);

  useEffect(() => {
    if (manifestData?.status === "COMPLETED") {
      setShowAddInventoryCard(false);
    }
  }, [manifestData?.status]);
  const [storageErrors, setStorageErrors] = useState<Set<string>>(new Set());

  interface PortalRow {
    quantity: string;
    targetLocation: string;
    selectedLocationId: string | null;
    selectedLocationWarehouseId: string | null;
    selectedLocationType: string;
    capacityWarning: string;
    suggestions: any[];
    palletSuggestions: any[];
    showSuggestions: boolean;
  }

  // Controlled states for Add Inventory Card
  const [addSku, setAddSku] = useState<string>("");
  const [isReserved, setIsReserved] = useState<boolean>(true);

  const [rows, setRows] = useState<PortalRow[]>([
    {
      quantity: "1",
      targetLocation: "",
      selectedLocationId: null,
      selectedLocationWarehouseId: null,
      selectedLocationType: "",
      capacityWarning: "",
      suggestions: [],
      palletSuggestions: [],
      showSuggestions: false,
    }
  ]);

  const [storageMeta, setStorageMeta] = useState({
    binCap: 9999,
    palletCap: 9999,
    supportsBins: true,
    supportsPallets: true,
  });

  const { mutate: verifySkuBarcode } = useVerifySkuMutation();
  const { mutate: verifyLocationBarcode } = useLocationBarcodeVerifyMutation();

  // Reset SKU-related states and rows when addSku changes
  useEffect(() => {
    setRows([
      {
        quantity: "1",
        targetLocation: "",
        selectedLocationId: null,
        selectedLocationWarehouseId: null,
        selectedLocationType: "",
        capacityWarning: "",
        suggestions: [],
        palletSuggestions: [],
        showSuggestions: false,
      }
    ]);
  }, [addSku]);

  // Reset SKU-related states and rows when isReserved changes
  useEffect(() => {
    setRows([
      {
        quantity: "1",
        targetLocation: "",
        selectedLocationId: null,
        selectedLocationWarehouseId: null,
        selectedLocationType: "",
        capacityWarning: "",
        suggestions: [],
        palletSuggestions: [],
        showSuggestions: false,
      }
    ]);
  }, [isReserved]);

  // Clear states when card visibility changes
  useEffect(() => {
    if (!showAddInventoryCard) {
      setAddSku("");
      setIsReserved(true);
      setRows([
        {
          quantity: "1",
          targetLocation: "",
          selectedLocationId: null,
          selectedLocationWarehouseId: null,
          selectedLocationType: "",
          capacityWarning: "",
          suggestions: [],
          palletSuggestions: [],
          showSuggestions: false,
        }
      ]);
    }
  }, [showAddInventoryCard]);

  const dropdownRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setRows((prev) => prev.map((r) => ({ ...r, showSuggestions: false })));
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLocationChange = (val: string, index: number) => {
    setRows((prev) =>
      prev.map((row, idx) => {
        if (idx === index) {
          return {
            ...row,
            targetLocation: val,
            selectedLocationId: null,
            selectedLocationWarehouseId: null,
            selectedLocationType: "",
            capacityWarning: "",
            showSuggestions: true,
          };
        }
        return row;
      })
    );
  };

  const handleQuantityChange = (val: string, index: number) => {
    setRows((prev) =>
      prev.map((row, idx) => {
        if (idx === index) {
          return {
            ...row,
            quantity: val,
            capacityWarning: "",
          };
        }
        return row;
      })
    );
  };

  const handleFetchLocationSuggestions = (index: number, overrideQty?: number) => {
    if (!addSku) {
      toast.error("Please select a SKU first");
      return;
    }
    const selectedProductForAdd = data?.products?.find((p: any) => p.sku === addSku);
    const inboundQty = selectedProductForAdd?.receivedQty || 0;
    const alreadyShelved = typeof selectedProductForAdd?.shelvedQty === "number"
      ? selectedProductForAdd.shelvedQty
      : (selectedProductForAdd?.isAddedToInventory ? inboundQty : 0);
    const remaining = Math.max(0, inboundQty - alreadyShelved);

    const row = rows[index];
    const qtyVal = overrideQty !== undefined
      ? overrideQty
      : (row.quantity ? parseInt(row.quantity) : (isReserved ? 0 : remaining));

    if (isReserved && qtyVal <= 0) {
      toast.error("Please enter a valid quantity first");
      return;
    }

    verifySkuBarcode(
      {
        sku: addSku,
        location: "",
        qty: qtyVal || 1,
        inventoryType: isReserved ? "reserved" : "received",
        warehouseId: data?.merchantWarehouseId || data?.locationId || undefined,
        excludeLocationIds: [],
        expiryDate: selectedProductForAdd?.expiryDate || undefined,
      },
      {
        onSuccess: (res: any) => {
          let storageArray = [];
          if (res?.storage) {
            if (typeof res.storage === "string") {
              try {
                storageArray = [JSON.parse(res.storage)];
              } catch (e) {
                console.error("Failed to parse storage string:", e);
              }
            } else if (Array.isArray(res.storage)) {
              storageArray = res.storage;
            } else if (typeof res.storage === "object") {
              storageArray = [res.storage];
            }
          }

          const binStorage = storageArray?.find(
            (s: any) => s?.type === "BINS" || s?.storageType === "BINS"
          );
          const palletStorage = storageArray?.find(
            (s: any) => s?.type === "PALLETS" || s?.storageType === "PALLETS"
          );

          setStorageMeta({
            binCap: binStorage ? parseInt(binStorage.capacity) || 0 : 9999,
            palletCap: palletStorage ? parseInt(palletStorage.capacity) || 0 : 9999,
            supportsBins: !!binStorage,
            supportsPallets: !!palletStorage,
          });

          const binSuggestions = res?.suggestedBins || [];
          const palletSuggestionsList = res?.suggestedPallets || [];

          setRows((prev) =>
            prev.map((r, idx) => {
              if (idx === index) {
                return {
                  ...r,
                  suggestions: binSuggestions,
                  palletSuggestions: palletSuggestionsList,
                  showSuggestions: true,
                };
              }
              return r;
            })
          );
          toast.success("Suggestions loaded");
        },
        onError: (err: any) => {
          toast.error(err?.message || "Failed to load suggestions");
        },
      }
    );
  };

  const handleVerifyLocationBarcode = (barcode: string, index: number) => {
    if (!barcode) return;
    const selectedProductForAdd = data?.products?.find((p: any) => p.sku === addSku);
    verifyLocationBarcode(
      {
        barcodeId: barcode,
        inventoryType: isReserved ? "reserved" : "received",
        expiryDate: selectedProductForAdd?.expiryDate || null,
        warehouseId: data?.merchantWarehouseId || data?.locationId || undefined,
      } as any,
      {
        onSuccess: (res: any) => {
          const barcodeLabel = res?.barcodeLabel || barcode;
          const availableQty = parseInt(res?.availableQty ?? "0");
          const locType = res?.locationType || "";

          const unitCap = locType === "PALLETS" ? storageMeta.palletCap : storageMeta.binCap;
          const qty = parseInt(rows[index].quantity) || 0;
          const freeSpace = unitCap - availableQty;
          const warning = (unitCap > 0 && qty > freeSpace)
            ? `Only ${Math.max(freeSpace, 0)} space available at "${barcodeLabel}".`
            : "";

          setRows((prev) =>
            prev.map((row, idx) => {
              if (idx === index) {
                return {
                  ...row,
                  targetLocation: barcodeLabel,
                  selectedLocationId: res?.id || null,
                  selectedLocationWarehouseId: res?.warehouseId || null,
                  selectedLocationType: locType,
                  capacityWarning: warning,
                };
              }
              return row;
            })
          );

          if (warning) {
            toast.error(warning);
          } else {
            toast.success("Location verified");
          }
        },
        onError: (err: any) => {
          toast.error(err?.message || String(err) || "Location verification failed");
          setRows((prev) =>
            prev.map((row, idx) => {
              if (idx === index) {
                return {
                  ...row,
                  targetLocation: "",
                  selectedLocationId: null,
                  selectedLocationWarehouseId: null,
                  selectedLocationType: "",
                  capacityWarning: "",
                };
              }
              return row;
            })
          );
        },
      }
    );
  };

  const handleSelectSuggestion = (loc: any, locType: "BINS" | "PALLETS", index: number) => {
    const unitCap = locType === "PALLETS" ? storageMeta.palletCap : storageMeta.binCap;
    const qty = parseInt(rows[index].quantity) || 0;
    const freeSpace = unitCap - (loc.currentInventoryCount || 0);
    const warning = (unitCap > 0 && qty > freeSpace)
      ? `Only ${Math.max(freeSpace, 0)} space available at "${loc.barcodeLabel}".`
      : "";

    setRows((prev) =>
      prev.map((row, idx) => {
        if (idx === index) {
          return {
            ...row,
            targetLocation: loc.barcodeLabel,
            selectedLocationId: loc.id,
            selectedLocationWarehouseId: loc.warehouseId,
            selectedLocationType: locType,
            capacityWarning: warning,
            showSuggestions: false,
          };
        }
        return row;
      })
    );

    if (!warning) {
      toast.success("Location selected");
    }
  };

  const handleAutoDistribute = (index: number) => {
    const row = rows[index];
    const qty = parseInt(row.quantity) || 0;
    if (qty <= 0) return;

    const isPalletMode = row.selectedLocationType === "PALLETS" || (!storageMeta.supportsBins && storageMeta.supportsPallets);
    const availableTargets = isPalletMode ? row.palletSuggestions : row.suggestions;
    const unitCap = isPalletMode ? storageMeta.palletCap : storageMeta.binCap;

    if (availableTargets.length === 0) {
      toast.error("No suggestions available for distribution.");
      return;
    }

    let remaining = qty;
    const updatedRows = [...rows];
    const newSplits: PortalRow[] = [];

    if (row.targetLocation) {
      const currentLocSuggestion = availableTargets.find((t) => t.barcodeLabel === row.targetLocation);
      const currentLocInventory = currentLocSuggestion ? currentLocSuggestion.currentInventoryCount : 0;
      const freeSpaceInCurrent = Math.max(0, unitCap - currentLocInventory);
      if (freeSpaceInCurrent > 0) {
        const take = Math.min(remaining, freeSpaceInCurrent);
        updatedRows[index] = {
          ...row,
          quantity: String(take),
          selectedLocationId: currentLocSuggestion ? currentLocSuggestion.id : row.selectedLocationId,
          selectedLocationWarehouseId: currentLocSuggestion ? currentLocSuggestion.warehouseId : row.selectedLocationWarehouseId,
          selectedLocationType: isPalletMode ? "PALLETS" : "BINS",
          capacityWarning: "",
        };
        remaining -= take;
      }
    }

    let targetIdx = 0;
    while (remaining > 0 && targetIdx < availableTargets.length) {
      const target = availableTargets[targetIdx];
      const locationAlreadyUsed =
        updatedRows.some((r) => r.targetLocation === target.barcodeLabel) ||
        newSplits.some((s) => s.targetLocation === target.barcodeLabel);
      if (locationAlreadyUsed) {
        targetIdx++;
        continue;
      }

      const targetFreeSpace = Math.max(0, unitCap - (target.currentInventoryCount || 0));
      if (targetFreeSpace > 0) {
        const take = Math.min(remaining, targetFreeSpace);
        newSplits.push({
          quantity: String(take),
          targetLocation: target.barcodeLabel,
          selectedLocationId: target.id,
          selectedLocationWarehouseId: target.warehouseId,
          selectedLocationType: isPalletMode ? "PALLETS" : "BINS",
          capacityWarning: "",
          suggestions: [],
          palletSuggestions: [],
          showSuggestions: false,
        });
        remaining -= take;
      }
      targetIdx++;
    }

    if (remaining > 0) {
      if (newSplits.length > 0) {
        newSplits[newSplits.length - 1].quantity = String(
          parseInt(newSplits[newSplits.length - 1].quantity) + remaining
        );
      } else {
        updatedRows[index].quantity = String(parseInt(updatedRows[index].quantity) + remaining);
      }
    }

    setRows([...updatedRows, ...newSplits]);
    toast.success("Quantity distributed into input rows.");
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        quantity: "1",
        targetLocation: "",
        selectedLocationId: null,
        selectedLocationWarehouseId: null,
        selectedLocationType: "",
        capacityWarning: "",
        suggestions: [],
        palletSuggestions: [],
        showSuggestions: false,
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, idx) => idx !== index));
  };

  const { mutate: addInventory, isPending: isAddingInventory } = useProductManifestAddInventoryMutation(id || "");

  const handleAddInventorySubmit = () => {
    if (!addSku) {
      toast.error("Please select a SKU");
      return;
    }

    const selectedProductForAdd = data?.products?.find((p: any) => p.sku === addSku);
    const inboundQty = selectedProductForAdd?.receivedQty || 0;
    const alreadyShelved = typeof selectedProductForAdd?.shelvedQty === "number"
      ? selectedProductForAdd.shelvedQty
      : (selectedProductForAdd?.isAddedToInventory ? inboundQty : 0);
    const remaining = Math.max(0, inboundQty - alreadyShelved);

    // Validate all rows
    const payloadProducts: any[] = [];
    let totalEntered = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.targetLocation) {
        toast.error(`Row ${i + 1}: Please enter or select a location.`);
        return;
      }
      if (!row.selectedLocationId) {
        toast.error(`Row ${i + 1}: Location "${row.targetLocation}" is not verified.`);
        return;
      }
      const qty = Number(row.quantity);
      if (isNaN(qty) || qty <= 0) {
        toast.error(`Row ${i + 1}: Please enter a valid quantity.`);
        return;
      }
      if (row.capacityWarning) {
        toast.error(`Row ${i + 1}: ${row.capacityWarning}`);
        return;
      }

      totalEntered += qty;
      payloadProducts.push({
        productId: selectedProductForAdd?.productId,
        sku: addSku,
        quantity: qty,
        expiryDate: selectedProductForAdd?.expiryDate || null,
        scannedLocation: row.targetLocation,
        scannedLocationId: row.selectedLocationId,
        scannedLocationWarehouseId: row.selectedLocationWarehouseId,
        inventoryType: isReserved ? "reserved" : "received",
      });
    }

    if (totalEntered > remaining && !isReserved) {
      toast.error(`Total quantity (${totalEntered}) exceeds remaining to be shelved (${remaining})`);
      return;
    }

    addInventory(
      {
        products: payloadProducts,
      },
      {
        onSuccess: () => {
          toast.success("Inventory added successfully");
          
          // Clear SKU selection only if it was inbound and is now fully completed
          const isCompleted = !isReserved && (totalEntered >= remaining);
          if (isCompleted) {
            setAddSku("");
          }
          
          setIsReserved(false);
          setRows([
            {
              quantity: "1",
              targetLocation: "",
              selectedLocationId: null,
              selectedLocationWarehouseId: null,
              selectedLocationType: "",
              capacityWarning: "",
              suggestions: [],
              palletSuggestions: [],
              showSuggestions: false,
            },
          ]);
          fetchDetails();
          fetchData();
        },
        onError: (err: any) => {
          toast.error(err?.message || String(err) || "Failed to add inventory");
        },
      }
    );
  };
  const [updatingSkuIndex, setUpdatingSkuIndex] = useState<number | null>(null);
  const { mutate: updateManifest, isPending: isUpdating } =
    useUpdateProductManifestAdminConfirm(id || "");
  const { mutate: addProducts, isPending: isAddingProducts } =
    useAddProductsToManifest(id || "");
  const { mutate: validateSkuStorage } = useValidateSkuStorageMutation();

  const handleQuantityBlur = (productId: string, sku: string) => {
    if (!productId) return;
    validateSkuStorage(
      { productId },
      {
        onSuccess: (res) => {
          if (!res?.hasStorage) {
            toast.error(
              `SKU ${sku} does not have storage type and capacity configured. Please add storage details for this product.`,
              { duration: 5000 },
            );
            // Add to storage errors state
            setStorageErrors((prev) => new Set(prev).add(sku));
          } else {
            // Remove from storage errors state if it exists
            setStorageErrors((prev) => {
              const newErrors = new Set(prev);
              newErrors.delete(sku);
              return newErrors;
            });
          }
        },
        onError: () => {
          // Silent or handle error if needed
        },
      },
    );
  };

  const addProductRow = () => {
    setNewProducts((prev) => [...prev, { id: Date.now(), productId: "" }]);
  };

  const removeProductRow = (id: number) => {
    setNewProducts((prev) => prev.filter((row) => row.id !== id));
  };

  const updateProductRow = (id: number, field: "productId", value: string) => {
    setNewProducts((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const {
    register,
    handleSubmit,
    setError,
    trigger,
    getValues,
    formState: { errors },
    reset,
  } = useForm<ProductForm>({
    defaultValues: {
      products: [],
    },
  });

  // Refetch data when modal opens or id changes
  useEffect(() => {
    if (open && id) {
      fetchDetails();
    }
  }, [open, id, fetchDetails]);

  // Reset form with fresh data
  useEffect(() => {
    if (data?.products) {
      console.log("453535", data.products);
      reset({
        products: data.products.map((product: ManifestProduct) => ({
          missingQty: product.missingQty ?? 0,
          damageQty: product.damageQty ?? 0,
          receivedQty: product.receivedQty ?? 0,
          note: product.adminReason ?? "",
          expiryDate: product.expiryDate ?? "",
        })),
      });
    }
  }, [data, reset]);

  // Reset storage errors when modal opens/closes or data changes
  useEffect(() => {
    setStorageErrors(new Set());
  }, [open, id, data]);

  if (!open || !id) return null;

  const hasQuantity =
    Array.isArray(data?.products) &&
    data?.products.some((item: any) => item?.quantity != null);

  const handleUpdateSingleProduct = async (i: number) => {
    // Trigger validation for just this product's fields
    const fieldsToValidate = [
      `products.${i}.receivedQty` as const,
      `products.${i}.missingQty` as const,
      `products.${i}.damageQty` as const,
      `products.${i}.adminReason` as const,
      `products.${i}.expiryDate` as const,
    ];

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const products = (data?.products as ManifestProduct[]) || [];
    const item = products[i];
    if (!item) return;

    const formData = getValues();
    const currentMissing = Number(formData.products?.[i]?.missingQty) || 0;
    const currentDamaged = Number(formData.products?.[i]?.damageQty) || 0;
    const currentReceived = Number(formData.products?.[i]?.receivedQty) || 0;
    const expectedQuantity = item?.quantity ?? 0;
    const totalLoss = currentMissing + currentDamaged;

    let hasError = false;

    // Validation: Received quantity cannot be less than already shelved quantity
    if (currentReceived < (item.shelvedQty ?? 0)) {
      setError(`products.${i}.receivedQty`, {
        type: "manual",
        message: `Received quantity (${currentReceived}) cannot be less than already shelved quantity (${item.shelvedQty})`,
      });
      toast.error(
        `Error: Received quantity for SKU ${item.sku} cannot be less than already shelved quantity (${item.shelvedQty}).`,
      );
      hasError = true;
    }

    // Validation: If product has expiry, expiry date should not be null
    if (item?.hasExpiry && !formData.products?.[i]?.expiryDate) {
      setError(`products.${i}.expiryDate`, {
        type: "manual",
        message: "Expiry date is required for products with expiry enabled",
      });
      hasError = true;
    }

    if (totalLoss > expectedQuantity) {
      setError(`products.${i}.missingQty`, {
        type: "manual",
        message: `Total loss (${totalLoss}) exceeds quantity (${expectedQuantity})`,
      });
      setError(`products.${i}.damageQty`, {
        type: "manual",
        message: `Total loss (${totalLoss}) exceeds quantity (${expectedQuantity})`,
      });
      toast.error(
        `Error: Loss for product ${item.sku} exceeds total quantity.`,
      );
      hasError = true;
    }

    const totalQuantitySum = currentReceived + currentMissing + currentDamaged;
    if (totalQuantitySum !== expectedQuantity) {
      setError(`products.${i}.receivedQty`, {
        type: "manual",
        message: `Sum of Rec. Qty., Missing, and Damage (${totalQuantitySum}) must equal Quantity (${expectedQuantity})`,
      });
      setError(`products.${i}.missingQty`, {
        type: "manual",
        message: `Sum of Rec. Qty., Missing, and Damage (${totalQuantitySum}) must equal Quantity (${expectedQuantity})`,
      });
      setError(`products.${i}.damageQty`, {
        type: "manual",
        message: `Sum of Rec. Qty., Missing, and Damage (${totalQuantitySum}) must equal Quantity (${expectedQuantity})`,
      });
      toast.error(
        `Error: For SKU ${item.sku}, the sum of Rec. Qty. (${currentReceived}) + Qty(Missing) (${currentMissing}) + Qty(Damage) (${currentDamaged}) = ${totalQuantitySum} must equal Quantity (${expectedQuantity}).`,
      );
      hasError = true;
    }

    if (hasError) return;

    setUpdatingSkuIndex(i);

    const productPayload = [{
      productId: item?.productId,
      quantity: expectedQuantity,
      missingQty: currentMissing,
      damageQty: currentDamaged,
      receivedQty: currentReceived,
      adminReason: formData.products?.[i]?.adminReason ?? "",
      expiryDate: formData.products?.[i]?.expiryDate ?? item?.expiryDate ?? "",
      movementType: currentMissing > 0 || currentDamaged > 0 ? "Adjustment" : "Inbound",
      note: item?.note ?? "",
    }];

    const allOtherSkusVerified = products.every((p: any, idx: number) => {
      if (idx === i) return true;
      const hasMovement = !!p.movementType;
      const isValidSum = ((p.receivedQty ?? 0) + (p.missingQty ?? 0) + (p.damageQty ?? 0)) === (p.quantity ?? 0);
      return hasMovement || isValidSum;
    });

    const currentStatus = manifestData?.status;
    const newStatus = (() => {
      if (
        currentStatus === "ASSIGN_TO_INVENTORY_STAFF" ||
        currentStatus === "ADD_INVENTORY" ||
        currentStatus === "COMPLETED"
      ) {
        return currentStatus;
      }
      return allOtherSkusVerified ? "ON_HOLD" : "ON_PROCESS";
    })();

    const payload = {
      status: isEditableStatus ? newStatus : manifestData?.status,
      products: productPayload,
    };

    updateManifest(payload, {
      onSuccess: () => {
        setUpdatingSkuIndex(null);
        toast.success(`SKU ${item.sku} updated successfully`);
        fetchDetails();
        fetchData();
      },
      onError: (error: any) => {
        setUpdatingSkuIndex(null);
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred.";
        toast.error(errorMessage);
      },
    });
  };

  const onSubmit = (formData: ProductForm) => {
    // Handle new products submission for MANIFEST_REQUEST status
    if (manifestData?.status === "MANIFEST_REQUEST") {
      const validNewProducts = newProducts.filter((p) => p.productId);

      if (validNewProducts.length === 0) {
        toast.error("Please add at least one product");
        return;
      }

      const payload = {
        product: validNewProducts.map((p) => ({
          productId: p.productId,
        })),
      };

      addProducts(payload, {
        onSuccess: () => {
          onClose();
          toast.success("Products added successfully");
          fetchDetails();
          fetchData();
          setShowAddProduct(false);
          setNewProducts([{ id: Date.now(), productId: "" }]);
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.message ||
            error.message ||
            "An unknown error occurred.";
          toast.error(errorMessage);
        },
      });
      return;
    }

    // Original logic for other statuses
    if (type !== "update") return;

    const products = (data?.products as ManifestProduct[]) || [];
    if (!products.length) return;

    let hasError = false;
    let hasLoss = false;
    const productPayload: any[] = [];

    products.forEach((item, i) => {
      const currentMissing = Number(formData.products?.[i]?.missingQty) || 0;
      const currentDamaged = Number(formData.products?.[i]?.damageQty) || 0;
      const currentReceived = Number(formData.products?.[i]?.receivedQty) || 0;
      const expectedQuantity = item?.quantity ?? 0;
      const totalLoss = currentMissing + currentDamaged;

      // Validation: Received quantity cannot be less than already shelved quantity
      if (currentReceived < (item.shelvedQty ?? 0)) {
        setError(`products.${i}.receivedQty`, {
          type: "manual",
          message: `Received quantity (${currentReceived}) cannot be less than already shelved quantity (${item.shelvedQty})`,
        });
        toast.error(
          `Error: Received quantity for SKU ${item.sku} cannot be less than already shelved quantity (${item.shelvedQty}).`,
        );
        hasError = true;
      }

      // Validation: If product has expiry, expiry date should not be null
      if (item?.hasExpiry && !formData.products?.[i]?.expiryDate) {
        setError(`products.${i}.expiryDate`, {
          type: "manual",
          message: "Expiry date is required for products with expiry enabled",
        });
        hasError = true;
      }

      if (currentMissing > 0 || currentDamaged > 0) {
        hasLoss = true;
      }

      if (totalLoss > expectedQuantity) {
        setError(`products.${i}.missingQty`, {
          type: "manual",
          message: `Total loss (${totalLoss}) exceeds quantity (${expectedQuantity})`,
        });
        setError(`products.${i}.damageQty`, {
          type: "manual",
          message: `Total loss (${totalLoss}) exceeds quantity (${expectedQuantity})`,
        });
        toast.error(
          `Error: Loss for product ${item.sku} exceeds total quantity.`,
        );
        hasError = true;
      }

      const totalSum = currentMissing + currentDamaged;

      if (totalSum > expectedQuantity) {
        setError(`products.${i}.missingQty`, {
          type: "manual",
          message: `Total loss (${totalSum}) exceeds quantity (${expectedQuantity})`,
        });
        setError(`products.${i}.damageQty`, {
          type: "manual",
          message: `Total loss (${totalSum}) exceeds quantity (${expectedQuantity})`,
        });

        toast.error(
          `Error: Loss for product ${item.sku} exceeds total quantity.`,
        );

        hasError = true;
      }

      // Validation: Rec. Qty. + Qty(Missing) + Qty(Damage) must equal Quantity
      const totalQuantitySum =
        currentReceived + currentMissing + currentDamaged;
      if (totalQuantitySum !== expectedQuantity) {
        setError(`products.${i}.receivedQty`, {
          type: "manual",
          message: `Sum of Rec. Qty., Missing, and Damage (${totalQuantitySum}) must equal Quantity (${expectedQuantity})`,
        });
        setError(`products.${i}.missingQty`, {
          type: "manual",
          message: `Sum of Rec. Qty., Missing, and Damage (${totalQuantitySum}) must equal Quantity (${expectedQuantity})`,
        });
        setError(`products.${i}.damageQty`, {
          type: "manual",
          message: `Sum of Rec. Qty., Missing, and Damage (${totalQuantitySum}) must equal Quantity (${expectedQuantity})`,
        });
        toast.error(
          `Error: For SKU ${item.sku}, the sum of Rec. Qty. (${currentReceived}) + Qty(Missing) (${currentMissing}) + Qty(Damage) (${currentDamaged}) = ${totalQuantitySum} must equal Quantity (${expectedQuantity}).`,
        );
        hasError = true;
      }

      productPayload.push({
        productId: item?.productId,
        quantity: expectedQuantity,
        missingQty: currentMissing,
        damageQty: currentDamaged,
        receivedQty: currentReceived,
        adminReason: formData.products?.[i]?.adminReason ?? "",
        expiryDate:
          formData.products?.[i]?.expiryDate ?? item?.expiryDate ?? "",
        movementType:
          currentMissing > 0 || currentDamaged > 0 ? "Adjustment" : "Inbound",
        note: item?.note ?? "",
      });
    });

    if (hasError) return;
    const currentStatus = data?.status;
    const resolvedStatus = (() => {
      if (
        currentStatus === "ASSIGN_TO_INVENTORY_STAFF" ||
        currentStatus === "ADD_INVENTORY" ||
        currentStatus === "COMPLETED"
      ) {
        return currentStatus;
      }
      return "ON_HOLD";
    })();

    const payload = {
      status: isEditableStatus ? resolvedStatus : data?.status,
      product: productPayload,
    };

    updateManifest(payload, {
      onSuccess: () => {
        onClose();
        toast.success(
          `Manifest set to On Hold successfully`,
        );
        fetchDetails();
        fetchData();
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred.";
        toast.error(errorMessage);
      },
    });
  };


  const statusMap: any = {
    MANIFEST_REQUEST: "#92400E",
    UPDATED: "#1E40AF",
    ASSIGN_TO_STAFF: "#9A3412",
    VERIFIED: "#5B21B6",
    ASSIGN_TO_INVENTORY_STAFF: "#166534",
    ADD_TO_INVENTORY: "#374151",
    ON_HOLD: "#EF4444",
    CHECKED: "#0284C7",
    CLOSED: "#0284C7",
    ON_PROCESS: "#0284C7",
  };

  const wrapperClass = isSidebar
    ? ""
    : "fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center p-4";

  const formClass = isSidebar
    ? "flex flex-col"
    : "rounded-3xl w-full md:w-10/12 lg:w-9/12 bg-white flex flex-col max-h-[90vh] overflow-hidden shadow-2xl";

  return (
    <div className={wrapperClass}>
      <form
        id="product-verify-form"
        onSubmit={handleSubmit(onSubmit)}
        className={formClass}
      >
        {isSidebar ? null : (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold text-gray-800">
                View Request: {data?.manifestShortId || "Manifest"}
              </h1>
              {isEditableStatus && (
                <button
                  type="button"
                  onClick={() => setIsEditingMode(!isEditingMode)}
                  className={`px-3 py-1.5 text-xs rounded-xl font-semibold transition-all border ${
                    isEditingMode
                      ? "bg-red-100 hover:bg-red-200 text-red-700 border-red-200"
                      : "bg-[#FBD037] hover:bg-yellow-400 text-black border-transparent shadow-sm"
                  }`}
                >
                  {isEditingMode
                    ? "Cancel"
                    : (data?.status === "VERIFIED" ||
                       data?.status === "ADD_INVENTORY" ||
                       data?.status === "COMPLETED"
                         ? "Edit Verify Qty"
                         : "Check & Verify")}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close modal"
            >
              <IconX size={20} />
            </button>
          </div>
        )}

        <div className={`flex-1 p-6 bg-white space-y-4 ${showAddInventoryCard ? "overflow-visible" : "overflow-y-auto"}`}>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <IconLoader2 className="w-8 h-8 animate-spin text-[#FBD037]" />
              <p className="text-sm text-slate-400">
                Loading manifest details...
              </p>
            </div>
          ) : !data ? (
            <div className="flex flex-col items-center justify-center py-20">
              <p className="text-sm text-slate-400">
                No data available for this manifest.
              </p>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="flex justify-between items-center  mb-4 border-b border-gray-200/50 pb-2">
                <div className="flex gap-2">
                  {(
                    [
                      {
                        key: "OVERVIEW",
                        icon: <IconInfoCircle size={18} />,
                        label: t("Overview"),
                      },
                      {
                        key: "CARGO ITEMS",
                        icon: <IconPackage size={18} />,
                        label: t("SKU"),
                      },
                      {
                        key: "ACTIVITY LOG",
                        icon: <IconHistory size={18} />,
                        label: t("Timeline"),
                      },
                      {
                        key: "POD MEDIA",
                        icon: <IconPhoto size={18} />,
                        label: t("POD Media"),
                      },
                    ] as const
                  ).map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 border ${
                        activeTab === tab.key
                          ? "bg-[#FBD037]/20 text-[#000000] border-gray-200 shadow-sm"
                          : "bg-slate-100 hover:bg-slate-200/60 text-slate-600 border-gray-200"
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {activeTab === "CARGO ITEMS" &&
                    (manifestData?.status === "VERIFIED" ||
                      manifestData?.status === "ASSIGN_TO_INVENTORY_STAFF" ||
                      manifestData?.status === "ADD_INVENTORY" ||
                      manifestData?.status === "IN_PROCESS" ||
                      manifestData?.status === "ON_HOLD") && (
                      <button
                        type="button"
                        onClick={() => setShowAddInventoryCard(!showAddInventoryCard)}
                        className="px-4 py-2 text-xs rounded-xl bg-[#FBD037] hover:bg-yellow-400 text-[#212121] font-medium transition-colors"
                      >
                        {showAddInventoryCard ? "Close" : "Add Inventory"}
                      </button>
                    )}
                </div>
              </div>

              {activeTab === "ACTIVITY LOG" && (
                <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                  <div className="relative">
                    {/* <SectionHeader icon={IconHistory} title="Timeline" />{" "} */}
                    {manifestData?.statusHistories &&
                    manifestData.statusHistories.length > 0 ? (
                      <div className="space-y-0 relative pl-4 border-l border-emerald-200/60 ml-2">
                        {manifestData.statusHistories.map(
                          (
                            item: StatusHistory,
                            index: number,
                            array: StatusHistory[],
                          ) => {
                            const isLast = index === array.length - 1;
                            return (
                              <div
                                key={item.id || index}
                                className="relative pb-6 last:pb-0"
                              >
                                {/* Dot Indicator */}
                                {isLast ? (
                                  <div className="absolute -left-[24px] top-1 w-4 h-4 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-md border border-white ring-2 ring-emerald-600/30">
                                    <IconCheck size={10} stroke={4} />
                                  </div>
                                ) : (
                                  <div className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white ring-2 ring-emerald-500/20 shadow-xs" />
                                )}
                                <div>
                                  <div className="flex justify-between items-center">
                                    <p className="text-xs font-semibold text-slate-800">
                                      {t(item?.newStatus
                                        ?.replace(/_/g, " ")
                                        .toLowerCase()
                                        .replace(/\b\w/g, (char: string) =>
                                          char.toUpperCase(),
                                        ))}
                                    </p>
                                    {item?.createdAt && (
                                      <p className="text-[10px] text-slate-400 mt-1">
                                        {formatDateWithTime(item.createdAt)}
                                      </p>
                                    )}
                                  </div>
                                  {item?.details && (
                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                      <AsyncT text={item.details} />
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <div className="py-8 text-center text-xs text-slate-400">
                        No activity history logs found.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "OVERVIEW" && (
                <div className="space-y-4">
                  {/* Manifest Specifications Card */}
                  {/* <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                    <SectionHeader icon={IconClipboardText} title="Manifest Specifications" />
                    <div className="space-y-2 relative">
                      <Row label="Manifest ID" value={data?.manifestShortId} />
                      <Row
                        label="Status"
                        value={
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${data?.status === "COMPLETED"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-blue-50 text-blue-700 border-blue-200"
                            }`}>
                            {(data?.status || "").replace(/_/g, " ")}
                          </span>
                        }
                      />
                      <Row label="Client ID" value={data?.clientId} />
                    </div>
                  </div> */}

                  {/* Client Info Card */}
                  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md">
                    <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
                      <SectionHeader
                        className=""
                        icon={IconUserCheck}
                        title={t("Client")}
                      />
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                      <Row value={data?.clientName} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Row
                        icon={<IconPhone size={16} />}
                        label="Mobile"
                        value={data?.clientMobileNo}
                      />
                      <Row
                        icon={<IconMail size={16} />}
                        label="Email"
                        value={data?.clientEmail}
                      />
                    </div>
                  </div>

                  {/* Assigned Check Staff Card */}
                  {data?.staffName && (
                    <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md relative overflow-hidden">
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-blue-200/10 to-transparent pointer-events-none" />
                      <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
                        <SectionHeader
                          className=""
                          icon={IconUser}
                          title={t("Assigned Check Staff")}
                        />
                        <Row value={data?.staffName} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Row
                          icon={<IconPhone size={16} />}
                          label="Mobile"
                          value={data?.staffPhone}
                        />
                        <Row
                          icon={<IconMail size={16} />}
                          label="Email"
                          value={data?.staffEmail}
                        />
                      </div>
                    </div>
                  )}

                  {/* Assigned Inventory Staff Card */}
                  {data?.inventoryStaffName && (
                    <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 shadow-sm hover:shadow-md relative overflow-hidden">
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-green-200/10 to-transparent pointer-events-none" />
                      <div className="flex justify-between items-center border-b border-[#FBD037]/30 pb-2 mb-3">
                        <SectionHeader
                          className=""
                          icon={IconUser}
                          title={t("Assigned Inventory Staff")}
                        />
                        <Row value={data?.inventoryStaffName} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Row
                          icon={<IconPhone size={16} />}
                          label="Mobile"
                          value={data?.inventoryStaffPhone}
                        />
                        <Row
                          icon={<IconMail size={16} />}
                          label="Email"
                          value={data?.inventoryStaffEmail}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "CARGO ITEMS" && (
                <div className="space-y-4">
                  <div className="w-full mt-2">
                    {Array.isArray(data?.products) &&
                    data?.products.length > 0 ? (
                      (() => {
                        const mergedMap = new Map<string, any>();

                        data.products.forEach((item: ManifestProduct) => {
                          const sku = item?.sku || "";
                          if (mergedMap.has(sku)) {
                            const existing = mergedMap.get(sku);
                            mergedMap.set(sku, {
                              ...existing,
                              quantity:
                                (existing.quantity || 0) + (item.quantity || 0),
                              missingQty:
                                (existing.missingQty || 0) +
                                (item.missingQty || 0),
                              damageQty:
                                (existing.damageQty || 0) +
                                (item.damageQty || 0),
                              receivedQty:
                                (existing.receivedQty || 0) +
                                (item.receivedQty || 0),
                              reservedQuantity:
                                (existing.reservedQuantity || 0) +
                                (item.reservedQuantity || 0),
                              note: existing.note || item.note,
                            });
                          } else {
                            mergedMap.set(sku, { ...item });
                          }
                        });

                        const mergedProducts = Array.from(mergedMap.values());

                        return (
                          <div className="space-y-2">
                            {showAddInventoryCard && (
                              <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3 relative z-30">
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                                <div className="flex justify-between items-center gap-8 border-b border-[#FBD037]/30 pb-2 mb-3 z-10 relative">
                                  <SectionHeader
                                    icon={IconBox}
                                    title="Add inventory"
                                    className="whitespace-nowrap"
                                  />
                                  <div className="flex items-center gap-2 w-full">
                                    <NewSelectInput
                                      className="flex items-center gap-4 w-full"
                                      name="sku"
                                      label="SKU"
                                      placeholder="Select"
                                      value={addSku}
                                      onChange={(val) => {
                                        setAddSku(val);
                                      }}
                                      selectOptions={
                                        mergedProducts?.map((p: any) => ({
                                          id: p?.sku,
                                          value: p?.sku,
                                          text: p?.sku,
                                        })) || []
                                      }
                                    />
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs">Reserved</span>
                                      <ToggleButton
                                        checked={!isReserved}
                                        onChange={(e: any) => {
                                          setIsReserved(!e.target.checked);
                                        }}
                                      />
                                      <span className="text-xs">Inbound</span>
                                    </div>
                                  </div>
                                </div>

                                {(() => {
                                  const selectedProductForAdd = addSku
                                    ? mergedProducts.find((p: any) => p.sku === addSku)
                                    : null;
                                  const inboundQty = selectedProductForAdd?.receivedQty || 0;
                                  const alreadyShelved = typeof selectedProductForAdd?.shelvedQty === "number"
                                    ? selectedProductForAdd.shelvedQty
                                    : (selectedProductForAdd?.isAddedToInventory ? inboundQty : 0);
                                  const remainingQty = Math.max(0, inboundQty - alreadyShelved);

                                  const totalEnteredQty = rows.reduce((sum, r) => sum + (parseInt(r.quantity) || 0), 0);
                                  const isOverRemainingLimit = addSku ? (!isReserved && totalEnteredQty > remainingQty) : false;

                                  return (
                                    <div className="grid grid-cols-3 gap-4 z-10 relative">
                                      <div className="col-span-3 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl p-3 flex justify-between my-1">
                                        <span>Inbound Qty: <strong className="text-slate-900">{addSku ? inboundQty : 0}</strong></span>
                                        <span>Already Shelved: <strong className="text-slate-900">{addSku ? alreadyShelved : 0}</strong></span>
                                        <span>Remaining: <strong className="text-blue-600 font-bold">{addSku ? remainingQty : 0}</strong></span>
                                      </div>

                                      {isOverRemainingLimit && (
                                        <div className="col-span-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl p-3 font-semibold my-1">
                                          Total entered quantity ({totalEnteredQty}) exceeds the remaining manifest quantity ({remainingQty}).
                                        </div>
                                      )}

                                      <div className="col-span-3 space-y-4 overflow-visible" ref={dropdownRef}>
                                        {rows.map((row, idx) => {
                                          const isMultiRow = rows.length > 1;
                                          return (
                                            <div key={idx} className="space-y-2 relative" style={{ zIndex: 100 - idx }}>
                                              <div className={isMultiRow ? "grid grid-cols-[1fr_1fr_40px] gap-2 items-end" : "grid grid-cols-[1fr_1fr] gap-2 items-end"}>
                                                {isReserved ? (
                                                  <>
                                                    <NewinputField
                                                      name={`addQty-${idx}`}
                                                      title={idx === 0 ? "Intake Quantity" : ""}
                                                      type="number"
                                                      min="1"
                                                      placeholder="Qty"
                                                      value={row.quantity}
                                                      disabled={!addSku}
                                                      other={{
                                                        onChange: (e: any) => {
                                                          const val = e.target.value;
                                                          if (val === "" || Number(val) > 0) {
                                                            handleQuantityChange(val, idx);
                                                          }
                                                        },
                                                        onKeyDown: (e: any) => {
                                                          if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleFetchLocationSuggestions(idx);
                                                          }
                                                        }
                                                      }}
                                                      inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                                                    />

                                                    <div className="relative">
                                                      <NewinputField
                                                        name={`addLocation-${idx}`}
                                                        title={idx === 0 ? "Target Location" : ""}
                                                        type="text"
                                                        placeholder="Enter Location"
                                                        value={row.targetLocation}
                                                        disabled={!addSku || !row.quantity || parseInt(row.quantity) <= 0}
                                                        other={{
                                                          onChange: (e: any) => handleLocationChange(e.target.value, idx),
                                                          onKeyDown: (e: any) => {
                                                            if (e.key === "Enter") {
                                                              e.preventDefault();
                                                              handleVerifyLocationBarcode(e.currentTarget.value, idx);
                                                            }
                                                          },
                                                          onClick: () => {
                                                            if (!addSku || !row.quantity || parseInt(row.quantity) <= 0 || isOverRemainingLimit) return;
                                                            if (row.suggestions.length > 0 || row.palletSuggestions.length > 0) {
                                                              setRows(prev => prev.map((r, rIdx) => rIdx === idx ? { ...r, showSuggestions: true } : r));
                                                            } else {
                                                              handleFetchLocationSuggestions(idx);
                                                            }
                                                          }
                                                        }}
                                                        inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                                                      />

                                                      {row.showSuggestions && !isOverRemainingLimit && (row.suggestions.length > 0 || row.palletSuggestions.length > 0) && (
                                                        <div className="absolute left-0 top-full w-full max-h-60 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-[0_12px_40px_-10px_rgba(0,0,0,0.2)] z-[500] py-0 custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200">
                                                          <div className="sticky top-0 bg-white px-4 py-2 flex items-center justify-between border-b border-gray-50 mb-1 z-10">
                                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                              Suggested Locations
                                                            </span>
                                                            <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-md font-bold">
                                                              {row.suggestions.length + row.palletSuggestions.length} found
                                                            </span>
                                                          </div>

                                                          {row.suggestions.length > 0 && (
                                                            <>
                                                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Bins</div>
                                                              {row.suggestions.map((loc: any) => (
                                                                <button
                                                                  key={loc.barcodeLabel}
                                                                  type="button"
                                                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                                                  onMouseDown={(e) => {
                                                                    e.preventDefault();
                                                                    handleSelectSuggestion(loc, "BINS", idx);
                                                                  }}
                                                                >
                                                                  <div className="flex flex-col">
                                                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                                                      {loc.barcodeLabel}
                                                                    </span>
                                                                    <span className="text-[10px] text-gray-400">
                                                                      {loc.currentInventoryCount} items stored
                                                                      <span className="text-blue-600 font-semibold ml-1">
                                                                        ({Math.max(0, storageMeta.binCap - loc.currentInventoryCount)} left)
                                                                      </span>
                                                                    </span>
                                                                  </div>
                                                                </button>
                                                              ))}
                                                            </>
                                                          )}

                                                          {row.palletSuggestions.length > 0 && (
                                                            <>
                                                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Pallets</div>
                                                              {row.palletSuggestions.map((loc: any) => (
                                                                <button
                                                                  key={loc.barcodeLabel}
                                                                  type="button"
                                                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                                                  onMouseDown={(e) => {
                                                                    e.preventDefault();
                                                                    handleSelectSuggestion(loc, "PALLETS", idx);
                                                                  }}
                                                                >
                                                                  <div className="flex flex-col">
                                                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                                                      {loc.barcodeLabel}
                                                                    </span>
                                                                    <span className="text-[10px] text-gray-400">
                                                                      {loc.currentInventoryCount} items stored
                                                                      <span className="text-blue-600 font-semibold ml-1">
                                                                        ({Math.max(0, storageMeta.palletCap - loc.currentInventoryCount)} left)
                                                                      </span>
                                                                    </span>
                                                                  </div>
                                                                </button>
                                                              ))}
                                                            </>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  </>
                                                ) : (
                                                  <>
                                                    <div className="relative">
                                                      <NewinputField
                                                        name={`addLocation-${idx}`}
                                                        title={idx === 0 ? "Target Location" : ""}
                                                        type="text"
                                                        placeholder="Enter Location"
                                                        value={row.targetLocation}
                                                        disabled={!addSku}
                                                        other={{
                                                          onChange: (e: any) => handleLocationChange(e.target.value, idx),
                                                          onKeyDown: (e: any) => {
                                                            if (e.key === "Enter") {
                                                              e.preventDefault();
                                                              handleVerifyLocationBarcode(e.currentTarget.value, idx);
                                                            }
                                                          },
                                                          onClick: () => {
                                                            if (!addSku || isOverRemainingLimit) return;
                                                            if (row.suggestions.length > 0 || row.palletSuggestions.length > 0) {
                                                              setRows(prev => prev.map((r, rIdx) => rIdx === idx ? { ...r, showSuggestions: true } : r));
                                                            } else {
                                                              handleFetchLocationSuggestions(idx);
                                                            }
                                                          }
                                                        }}
                                                        inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                                                      />

                                                      {row.showSuggestions && !isOverRemainingLimit && (row.suggestions.length > 0 || row.palletSuggestions.length > 0) && (
                                                        <div className="absolute left-0 top-full w-full max-h-60 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-[0_12px_40px_-10px_rgba(0,0,0,0.2)] z-[500] py-0 custom-scrollbar animate-in fade-in slide-in-from-top-2 duration-200">
                                                          <div className="sticky top-0 bg-white px-4 py-2 flex items-center justify-between border-b border-gray-50 mb-1 z-10">
                                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                              Suggested Locations
                                                            </span>
                                                            <span className="text-[10px] bg-yellow-50 text-yellow-700 px-1.5 py-0.5 rounded-md font-bold">
                                                              {row.suggestions.length + row.palletSuggestions.length} found
                                                            </span>
                                                          </div>

                                                          {row.suggestions.length > 0 && (
                                                            <>
                                                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Bins</div>
                                                              {row.suggestions.map((loc: any) => (
                                                                <button
                                                                  key={loc.barcodeLabel}
                                                                  type="button"
                                                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                                                  onMouseDown={(e) => {
                                                                    e.preventDefault();
                                                                    handleSelectSuggestion(loc, "BINS", idx);
                                                                  }}
                                                                >
                                                                  <div className="flex flex-col">
                                                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                                                      {loc.barcodeLabel}
                                                                    </span>
                                                                    <span className="text-[10px] text-gray-400">
                                                                      {loc.currentInventoryCount} items stored
                                                                      <span className="text-blue-600 font-semibold ml-1">
                                                                        ({Math.max(0, storageMeta.binCap - loc.currentInventoryCount)} left)
                                                                      </span>
                                                                    </span>
                                                                  </div>
                                                                </button>
                                                              ))}
                                                            </>
                                                          )}

                                                          {row.palletSuggestions.length > 0 && (
                                                            <>
                                                              <div className="px-4 py-1 bg-gray-50 text-[9px] font-bold text-gray-400 uppercase tracking-widest border-b">Pallets</div>
                                                              {row.palletSuggestions.map((loc: any) => (
                                                                <button
                                                                  key={loc.barcodeLabel}
                                                                  type="button"
                                                                  className="w-full text-left px-4 py-3 hover:bg-yellow-50 group transition-all flex items-center justify-between border-b border-gray-50 last:border-0"
                                                                  onMouseDown={(e) => {
                                                                    e.preventDefault();
                                                                    handleSelectSuggestion(loc, "PALLETS", idx);
                                                                  }}
                                                                >
                                                                  <div className="flex flex-col">
                                                                    <span className="text-sm font-bold text-gray-700 group-hover:text-yellow-700 transition-colors">
                                                                      {loc.barcodeLabel}
                                                                    </span>
                                                                    <span className="text-[10px] text-gray-400">
                                                                      {loc.currentInventoryCount} items stored
                                                                      <span className="text-blue-600 font-semibold ml-1">
                                                                        ({Math.max(0, storageMeta.palletCap - loc.currentInventoryCount)} left)
                                                                      </span>
                                                                    </span>
                                                                  </div>
                                                                </button>
                                                              ))}
                                                            </>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>

                                                    <NewinputField
                                                      name={`addQty-${idx}`}
                                                      title={idx === 0 ? "Intake Quantity" : ""}
                                                      type="number"
                                                      min="1"
                                                      placeholder="Qty"
                                                      value={row.quantity}
                                                      disabled={!addSku || !row.targetLocation}
                                                      other={{
                                                        onChange: (e: any) => {
                                                          const val = e.target.value;
                                                          if (val === "" || Number(val) > 0) {
                                                            handleQuantityChange(val, idx);
                                                          }
                                                        },
                                                        onKeyDown: (e: any) => {
                                                          if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            handleFetchLocationSuggestions(idx);
                                                          }
                                                        }
                                                      }}
                                                      inputClassName="w-full rounded-full px-4 py-2 text-sm outline-none bg-white border border-gray-200 text-[#1E1E1E]"
                                                    />
                                                  </>
                                                )}

                                                {isMultiRow && (
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRemoveRow(idx)}
                                                    className="w-10 h-10 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-500 rounded-full border border-red-200 transition-colors mb-0.5"
                                                    title="Remove Row"
                                                  >
                                                    <IconTrash size={16} />
                                                  </button>
                                                )}
                                              </div>

                                              {row.capacityWarning && (
                                                <div className="mt-1 p-3 bg-red-50/50 border border-red-200 rounded-xl space-y-2">
                                                  <p className="text-xs text-red-700 font-semibold flex items-center gap-1.5">
                                                    <span>{row.capacityWarning}</span>
                                                  </p>

                                                  <div className="flex flex-col gap-1.5 pt-1.5 border-t border-red-100">
                                                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                                                      Suggested {row.selectedLocationType === "PALLETS" ? "Pallets" : "Bins"}:
                                                    </p>
                                                    <div className="flex flex-wrap gap-1">
                                                      {(row.selectedLocationType === "PALLETS" ? row.palletSuggestions : row.suggestions)
                                                        ?.slice(0, 4)
                                                        .map((loc) => {
                                                          const cap = row.selectedLocationType === "PALLETS" ? storageMeta.palletCap : storageMeta.binCap;
                                                          const left = cap - loc.currentInventoryCount;
                                                          return (
                                                            <button
                                                              key={loc.barcodeLabel}
                                                              type="button"
                                                              onClick={() => handleSelectSuggestion(loc, row.selectedLocationType === "PALLETS" ? "PALLETS" : "BINS", idx)}
                                                              className="px-2 py-1 bg-white hover:bg-yellow-50 text-[10px] font-bold rounded border border-gray-200 transition text-gray-700 flex items-center gap-1"
                                                            >
                                                              {loc.barcodeLabel}
                                                              <span className="text-blue-600">({left} left)</span>
                                                            </button>
                                                          );
                                                        })}
                                                    </div>
                                                  </div>

                                                  <button
                                                    type="button"
                                                    onClick={() => handleAutoDistribute(idx)}
                                                    className="w-full py-2 bg-yellow-400 hover:bg-yellow-500 text-black rounded-full text-xs font-bold flex items-center justify-center gap-1.5 transition shadow-sm hover:shadow"
                                                  >
                                                    <IconLayersIntersect size={14} /> Auto-Distribute
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      <div className="col-span-3 flex gap-2">
                                        <button
                                          type="button"
                                          onClick={handleAddInventorySubmit}
                                          disabled={isAddingInventory || isOverRemainingLimit}
                                          className="w-full bg-[#FBD037] hover:bg-yellow-400 disabled:bg-gray-200 disabled:text-gray-400 text-black py-2 rounded-xl text-xs font-bold transition-colors shadow-sm flex items-center justify-center gap-2"
                                        >
                                          {isAddingInventory ? (
                                            <>
                                              <IconLoader2 className="animate-spin" size={14} />
                                              <span>Saving...</span>
                                            </>
                                          ) : (
                                            <span>Save Inventory</span>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            {!showAddInventoryCard && mergedProducts.map((item: any, i: number) => (
                              <div
                                key={item.sku || i}
                                className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3"
                              >
                                <div className="flex items-center justify-between border-b border-[#FBD037]/30 pb-2 mb-3">
                                  <SectionHeader
                                    className=""
                                    icon={IconPackage}
                                    title={`${t("SKU")} : ${item?.sku}`}
                                  />
                                  <div className="flex items-center gap-2">
                                    <div className="px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-semibold text-slate-700">
                                      {t("Qty")} : {formatQuantity(item?.quantity)}
                                    </div>
                                    {isUpdatingMode &&
                                       isEditableStatus && (
                                         <button
                                           type="button"
                                           onClick={() => handleUpdateSingleProduct(i)}
                                           disabled={
                                             isUpdating || storageErrors.size > 0
                                           }
                                           className="bg-[#FBD037] text-xs hover:bg-yellow-400 text-[#212121] rounded-lg font-medium py-1.5 px-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                           aria-label="Confirm manifest"
                                         >
                                           {isUpdating && updatingSkuIndex === i ? (
                                             <IconLoader2 className="w-5 h-5 animate-spin" />
                                           ) : (
                                             <span>Update</span>
                                           )}
                                         </button>
                                       )}
                                  </div>
                                </div>
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                                {/* Header */}

                                {/* Specifications */}
                                <div
                                  className={`grid ${
                                    item.hasExpiry
                                      ? "grid-cols-4"
                                      : "grid-cols-3"
                                  } gap-4`}
                                >
                                  <Row
                                    className="flex items-center gap-2"
                                    // classNameValue=""
                                    label="Received Qty"
                                    value={
                                      isUpdatingMode &&
                                      isEditableStatus ? (
                                        <input
                                          type="number"
                                          placeholder="Qty.."
                                          {...register(
                                            `products.${i}.receivedQty`,
                                          )}
                                          defaultValue={item.receivedQty ?? 0}
                                          className={`rounded-lg px-1 py-2 appearance-none text-xs border ${
                                            errors?.products?.[i]?.receivedQty
                                              ? "border-red-500"
                                              : "border-[#D8D8D8]"
                                          } focus:outline-none w-16`}
                                        />
                                      ) : (
                                        formatQuantity(item?.receivedQty)
                                      )
                                    }
                                  />

                                  <Row
                                    className="flex items-center gap-2"
                                    // classNameValue=""
                                    label="Missing Qty"
                                    value={
                                      isUpdatingMode &&
                                      isEditableStatus ? (
                                        <input
                                          type="number"
                                          placeholder="Qty.."
                                          {...register(
                                            `products.${i}.missingQty`,
                                            {
                                              required: "Required",
                                              min: {
                                                value: 0,
                                                message: "Min 0",
                                              },
                                              onBlur: (e) => {
                                                if (
                                                  e.target.value &&
                                                  e.target.value !== "0"
                                                ) {
                                                  handleQuantityBlur(
                                                    item?.productId,
                                                    item?.sku,
                                                  );
                                                }
                                              },
                                            },
                                          )}
                                          className={`rounded-lg px-1 py-2  text-xs border ${
                                            errors?.products?.[i]?.missingQty
                                              ? "border-red-500"
                                              : "border-[#D8D8D8]"
                                          } focus:outline-none w-16`}
                                        />
                                      ) : (
                                        formatQuantity(item?.missingQty)
                                      )
                                    }
                                  />

                                  <Row
                                    className="flex items-center gap-2"
                                    // classNameValue=""
                                    label="Damage Qty"
                                    value={
                                      isUpdatingMode &&
                                      isEditableStatus ? (
                                        <input
                                          type="number"
                                          placeholder="Qty.."
                                          {...register(
                                            `products.${i}.damageQty`,
                                            {
                                              validate: (val: any) => {
                                                const num = Number(val);
                                                if (
                                                  val === "" ||
                                                  val === undefined ||
                                                  val === null
                                                )
                                                  return true;
                                                return (
                                                  (!isNaN(num) && num >= 0) ||
                                                  "Must be non-negative"
                                                );
                                              },
                                              onBlur: (e) => {
                                                if (
                                                  e.target.value &&
                                                  e.target.value !== "0"
                                                ) {
                                                  handleQuantityBlur(
                                                    item?.productId,
                                                    item?.sku,
                                                  );
                                                }
                                              },
                                            },
                                          )}
                                          className={`rounded-lg px-1 py-2  text-xs border ${
                                            errors?.products?.[i]?.damageQty
                                              ? "border-red-500"
                                              : "border-[#D8D8D8]"
                                          } focus:outline-none w-16`}
                                        />
                                      ) : (
                                        formatQuantity(item?.damageQty)
                                      )
                                    }
                                  />
                                  {item.hasExpiry && (
                                    <Row
                                      className="flex items-center gap-2"
                                      label="Expiry Date"
                                      value={
                                        isUpdatingMode &&
                                        isEditableStatus &&
                                        item?.hasExpiry ? (
                                          <input
                                            type="date"
                                            {...register(
                                              `products.${i}.expiryDate`,
                                            )}
                                            defaultValue={
                                              item?.expiryDate
                                                ? new Date(item.expiryDate)
                                                    .toISOString()
                                                    .split("T")[0]
                                                : ""
                                            }
                                            className={`rounded-lg px-1 py-2 text-xs border ${
                                              errors?.products?.[i]?.expiryDate
                                                ? "border-red-500"
                                                : "border-[#D8D8D8]"
                                            } focus:outline-none w-32`}
                                          />
                                        ) : item?.expiryDate ? (
                                          new Date(item.expiryDate)
                                            .toISOString()
                                            .split("T")[0]
                                        ) : (
                                          ""
                                        )
                                      }
                                    />
                                  )}
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <Row
                                    className="flex items-center gap-2"
                                    classNameValue="w-[220px] truncate"
                                    label="Admin Reason"
                                    value={
                                      isUpdatingMode &&
                                      isEditableStatus ? (
                                        <input
                                          placeholder="Admin reason..."
                                          {...register(
                                            `products.${i}.adminReason`,
                                          )}
                                          className="rounded-lg px-1 py-2 text-xs border border-[#D8D8D8] focus:outline-none w-full"
                                        />
                                      ) : (
                                        item?.adminReason || ""
                                      )
                                    }
                                  />

                                  <Row
                                    className="flex items-center gap-2"
                                    classNameValue="w-[200px] truncate"
                                    label="Client Reason"
                                    value={item?.clientReason || ""}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    ) : (
                      <div className="py-8 text-center text-xs text-slate-400">
                        No products found
                      </div>
                    )}
                  </div>

                  {/* Product Addition Section */}
                  {showAddProduct &&
                    manifestData?.status === "MANIFEST_REQUEST" && (
                      <div className="relative rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
                        <h3 className="text-gray-950 font-bold text-xs uppercase tracking-wider border-b border-[#FBD037]/30 pb-2 mb-3">
                          Add Products
                        </h3>
                        <div className="space-y-2">
                          {newProducts.map((product, index) => (
                            <div
                              key={product.id}
                              className="flex items-end gap-2"
                            >
                              <div className="flex-1">
                                <SelectInput
                                  name={`newProduct-${product.id}`}
                                  label={index === 0 ? "Select Product" : ""}
                                  placeholder="Select Product"
                                  value={product.productId}
                                  onChange={(val) =>
                                    updateProductRow(
                                      product.id,
                                      "productId",
                                      val,
                                    )
                                  }
                                  selectOptions={
                                    products?.map((product: any) => ({
                                      id: product?.id,
                                      value: product?.id,
                                      text: product?.itemName,
                                    })) || []
                                  }
                                  className="w-full"
                                />
                              </div>
                              {index === newProducts.length - 1 ? (
                                <button
                                  type="button"
                                  onClick={addProductRow}
                                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center 
                               bg-[#FBD037]/20 text-black rounded-full hover:bg-[#FBD037] 
                               border border-[#FBD037] transition"
                                >
                                  <IconPlus size={20} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => removeProductRow(product.id)}
                                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center
                               bg-red-100 text-red-600 rounded-full hover:bg-red-200
                               border border-red-400 transition"
                                >
                                  <IconMinus size={20} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {activeTab === "POD MEDIA" && (
                <div className="space-y-4">
                  {data?.locations?.length > 0 ? (
                    <div className="w-full p-4 border border-[#D2D2D2]/70 rounded-2xl bg-gradient-to-b from-white/90 to-white/60 shadow-sm">
                      <SectionHeader icon={IconRoute} title={t("Locations")} />
                      <div className="w-full overflow-x-auto mt-2 border border-slate-200 rounded-xl bg-white">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/50">
                              <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                #
                              </th>
                              <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                {t("SKU(Product Name)")}
                              </th>
                              {hasQuantity && (
                                <>
                                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    {t("Quantity")}
                                  </th>
                                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    {t("Location")}
                                  </th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(data?.locations) &&
                            data?.locations.length > 0 ? (
                              data.locations.map(
                                (item: any, i: number) => (
                                  <tr
                                    key={i}
                                    className="hover:bg-[#FBD037]/10 transition-colors border-b border-dashed border-slate-200 last:border-0"
                                  >
                                    <td className="py-3 px-4 text-xs text-slate-800 text-center">
                                      {i + 1}
                                    </td>
                                    <td className="py-3 px-4 text-xs text-slate-800 font-medium">
                                      {item?.sku || ""}
                                    </td>
                                    {hasQuantity && (
                                      <>
                                        <td className="py-3 px-4 text-xs text-slate-800">
                                          {formatQuantity(item?.quantity)}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-800">
                                          <div className="flex flex-col items-start gap-1">
                                            {item?.location ? (
                                              <span className="font-semibold text-xs text-slate-700">
                                                {item.location} (
                                                {item?.locationType})
                                              </span>
                                            ) : (
                                              <span className="text-slate-400">
                                                —
                                              </span>
                                            )}
                                            <span
                                              className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                                item?.isReserved
                                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                                  : "bg-green-50 text-green-700 border-green-200"
                                              }`}
                                            >
                                              {item?.isReserved ? t("Reserved") : t("Inbound")}
                                            </span>
                                          </div>
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                ),
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={hasQuantity ? 4 : 2}
                                  className="text-center py-4 px-4 text-xs text-slate-400"
                                >
                                  {t("No products found")}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}

                  <div className="rounded-2xl border border-[#D2D2D2]/70 bg-gradient-to-b from-white/90 to-white/60 p-4 shadow-sm">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center border-b pb-2">
                      {t("Proof of Delivery verification photo")}
                    </h4>
                    <div className="flex flex-col items-center justify-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                      <div className="text-center py-4 text-slate-400 flex flex-col items-center gap-2">
                        <IconPhoto
                          size={32}
                          className="opacity-30 text-[#212121]"
                        />
                        <p className="text-xs italic">{t("No POD media available")}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!isSidebar && (
          <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-2 rounded-b-3xl bg-white flex-shrink-0">
            {isUpdatingMode && (
              <button
                type="submit"
                className="px-6 py-2 text-xs rounded-xl bg-[#FBD037] hover:bg-yellow-400 text-black font-semibold transition-colors shadow-sm hover:shadow"
              >
                Confirm
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                reset();
                onClose();
              }}
              className="px-6 py-2 text-xs rounded-xl border border-[#D2D2D2] hover:bg-gray-100 transition-colors font-semibold text-slate-600 hover:text-slate-900"
            >
              Close
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ViewProductRequestModal;
